const money = (v) => (v === null || v === undefined ? '—' : `${v < 0 ? '−' : ''}$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pct = (v) => (v === null || v === undefined ? '—' : `${v}%`);
const num = (v) => (v === null || v === undefined ? '—' : v.toLocaleString());

const Row = ({ label, value, formula, testid }) => (
  <div className="flex items-baseline justify-between gap-4 py-2.5 border-b border-matte/10 last:border-0">
    <div>
      <div className="text-sm text-matte">{label}</div>
      <div className="text-[11px] text-matte/50">{formula}</div>
    </div>
    <div className="font-serif-display text-2xl text-matte whitespace-nowrap" data-testid={testid}>{value}</div>
  </div>
);

export const PricingPanel = ({ pricing, updating }) => {
  const r = pricing.results;
  return (
    <section className="mt-6 rounded-2xl bg-matte text-ivory p-6 md:p-8" data-testid="pricing-panel" aria-live="polite">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Calculated estimates</div>
        <span className="text-[10px] uppercase tracking-[0.2em] text-ivory/50" data-testid="pricing-updating">{updating ? 'Updating…' : 'Based on your saved assumptions'}</span>
      </div>
      <div className="mt-4 rounded-xl bg-ivory text-matte px-5 py-2">
        <div className="pt-3 text-[10px] uppercase tracking-[0.22em] text-matte/50">Per sale</div>
        <Row label="Gross profit per sale" formula="price − direct cost" value={money(r.gross_profit_per_sale)} testid="calc-gross-profit" />
        <Row label="Gross margin" formula="gross profit ÷ price" value={pct(r.gross_margin_pct)} testid="calc-gross-margin" />
        <Row label="Markup on cost" formula="gross profit ÷ direct cost" value={pct(r.markup_pct_actual)} testid="calc-markup" />
        <Row label="Suggested price at your markup" formula="direct cost × (1 + markup %)" value={money(r.suggested_price)} testid="calc-suggested-price" />
        <div className="pt-5 text-[10px] uppercase tracking-[0.22em] text-matte/50">Per month (estimates)</div>
        <Row label="Revenue" formula="price × monthly sales" value={money(r.monthly_revenue)} testid="calc-monthly-revenue" />
        <Row label="Direct costs" formula="direct cost × monthly sales" value={money(r.monthly_direct_costs)} testid="calc-monthly-direct-costs" />
        <Row label="Gross profit" formula="revenue − direct costs" value={money(r.monthly_gross_profit)} testid="calc-monthly-gross-profit" />
        <Row label="Estimated net profit" formula="gross profit − fixed expenses · before income taxes and any expenses not entered" value={money(r.estimated_monthly_net)} testid="calc-monthly-net" />
        <Row label="Break-even sales per month" formula="fixed expenses ÷ gross profit per sale (rounded up)" value={num(r.break_even_sales)} testid="calc-break-even" />
        <Row label="Sales needed for your income goal" formula="(fixed expenses + goal) ÷ gross profit per sale (rounded up)" value={num(r.sales_for_income_goal)} testid="calc-sales-for-goal" />
      </div>
      {pricing.warnings.length > 0 && (
        <ul className="mt-5 space-y-2 text-sm text-ivory/80" data-testid="pricing-warnings">
          {pricing.warnings.map((w) => <li key={w} className="flex gap-2"><span className="text-gold">•</span>{w}</li>)}
        </ul>
      )}
      <p className="mt-6 text-[12px] leading-relaxed text-ivory/60" data-testid="pricing-disclaimer">
        <span className="text-gold uppercase tracking-[0.2em] text-[10px] block mb-1">Guidance</span>
        These are preliminary estimates calculated only from the numbers you entered. They are not predictions or guarantees of revenue or profit.
        Revenue is money in; gross profit is what&rsquo;s left after direct costs; estimated net profit is what&rsquo;s left after fixed expenses too, before income taxes and any costs you haven&rsquo;t entered.
      </p>
    </section>
  );
};
